import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Plus, Edit, Trash2 } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { checklistAPI } from '@/lib/api';

const MonthlyChecklist = () => {
  const [checklists, setChecklists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ title: '', description: '' });
  const monthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');

  useEffect(() => {
    loadChecklists();
  }, []);

  const loadChecklists = async () => {
    setIsLoading(true);
    try {
      const data = await checklistAPI.getAll('Monthly', monthStart);
      setChecklists(data);
    } catch (error) {
      toast.error('Failed to load monthly checklist');
      console.error('Error loading checklist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      const created = await checklistAPI.create({
        checklist_type: 'Monthly',
        title: newItem.title,
        description: newItem.description,
        date: monthStart
      });
      setChecklists([...checklists, created]);
      setNewItem({ title: '', description: '' });
      setIsAddDialogOpen(false);
      toast.success('Checklist item added');
    } catch (error) {
      toast.error('Failed to add item');
      console.error('Error adding item:', error);
    }
  };

  const handleToggleComplete = async (item) => {
    try {
      const updated = await checklistAPI.update(item.id, {
        is_completed: !item.is_completed,
        completed_at: !item.is_completed ? new Date().toISOString() : null
      });
      setChecklists(checklists.map(c => c.id === item.id ? updated : c));
    } catch (error) {
      toast.error('Failed to update item');
      console.error('Error updating item:', error);
    }
  };

  const handleEditItem = async () => {
    if (!editingItem.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      const updated = await checklistAPI.update(editingItem.id, {
        title: editingItem.title,
        description: editingItem.description
      });
      setChecklists(checklists.map(c => c.id === editingItem.id ? updated : c));
      setIsEditDialogOpen(false);
      setEditingItem(null);
      toast.success('Item updated');
    } catch (error) {
      toast.error('Failed to update item');
      console.error('Error updating item:', error);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await checklistAPI.delete(id);
      setChecklists(checklists.filter(c => c.id !== id));
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Failed to delete item');
      console.error('Error deleting item:', error);
    }
  };

  const completedCount = checklists.filter(c => c.is_completed).length;
  const progressPercentage = checklists.length > 0 ? (completedCount / checklists.length) * 100 : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading checklist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10">
          <CalendarIcon className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Monthly Checklist</h1>
          <p className="text-muted-foreground mt-1">{format(new Date(), 'MMMM yyyy')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{checklists.length}</div>
            <p className="text-sm text-muted-foreground">Total Tasks</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{completedCount}</div>
            <p className="text-sm text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">{progressPercentage.toFixed(0)}%</div>
            <p className="text-sm text-muted-foreground">Progress</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>This Month's Tasks</CardTitle>
              <CardDescription>Complete all monthly tasks</CardDescription>
            </div>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Task
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Monthly Task</DialogTitle>
                  <DialogDescription>Add a new task to this month's checklist</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
                  <div className="grid gap-2">
                    <Label>Task Title *</Label>
                    <Input
                      value={newItem.title}
                      onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                      placeholder="Enter task title"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Description</Label>
                    <Textarea
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      placeholder="Task details (optional)"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleAddItem}>Add Task</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {checklists.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No tasks for this month</p>
              <p className="text-muted-foreground">Add your first monthly task to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {checklists.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <Checkbox
                    checked={item.is_completed}
                    onCheckedChange={() => handleToggleComplete(item)}
                    className="mt-1"
                  />
                  <div className="flex-1 space-y-1">
                    <h3 className={`font-medium ${item.is_completed ? 'line-through text-muted-foreground' : ''}`}>
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className={`text-sm ${item.is_completed ? 'line-through text-muted-foreground' : 'text-muted-foreground'}`}>
                        {item.description}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingItem(item);
                        setIsEditDialogOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>Update task details</DialogDescription>
          </DialogHeader>
          {editingItem && (
            <div className="grid gap-4 py-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
              <div className="grid gap-2">
                <Label>Task Title *</Label>
                <Input
                  value={editingItem.title}
                  onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Description</Label>
                <Textarea
                  value={editingItem.description || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditItem}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthlyChecklist;